import React, { useCallback, useEffect, useState, useRef } from 'react';
import { X, UserIcon } from 'lucide-react';
type TeamMember = {
  name: string;
  role: string;
  bio: string;
  photo?: string;
};
const teamMembers: Record<string, TeamMember> = {
  francesco: {
    name: 'Francesco Conrotto Caruso',
    role: 'Principal, Firetree Group',
    photo: 'https://cdn.magicpatterns.com/uploads/6xpoVugw3tHB3KMaK8S8wr/Fran_New.jpg',
    bio: "Francesco Conrotto Caruso has been involved in philanthropy in Asia for over 23 years. As Principal of the Firetree Group, he oversees its philanthropic giving and programmes, and since 2003 has overseen the disbursement of over US$130 million in grants. He divides his time between Singapore, the Philippines, Thailand, Malaysia, and Cambodia, engaging with local organisations as a director, advisor, thought partner, and funder.\n\nOver the course of his philanthropic career, Francesco has co-built, restructured, and managed a range of non-profits. In the Philippines, he co-founded and continues to manage the Tondo Community Initiative, a social work and child protection organisation based in Tondo, Manila. After relocating to Singapore from Hong Kong in 2020, he co-founded the Asia Community Foundation and serves as a director of Asia Philanthropy Circle. Earlier in his career, Francesco worked in finance and business development in Europe and North America. He holds a Law degree in International Business Law and a Master's degree in Management."
  },
  sreya: {
    name: 'Sreya Bhattacharya',
    role: 'Fund Manager, Firetree Fund',
    photo: 'https://cdn.magicpatterns.com/uploads/pooPbsRAjZtmWpFVUvr8Ly/Sreya_headshot.jpg',
    bio: 'Sreya Bhattacharya manages the Firetree Fund, bringing over 15 years of experience in philanthropy, grantmaking, and social impact across Asia. She has worked closely with NGOs and civil society organisations across South Asia — supporting strategy, organisational development, and programmatic design — and has a strong track record of translating funder priorities into meaningful, field-level impact. Prior to managing the Fund, Sreya served as an Associate Partner at Dalberg, and held roles at the Rockefeller Foundation and Dasra. Earlier in her career, she worked at KPMG advising private equity clients on organisational due diligence. She holds an MBA from Columbia Business School.'
  },
  nicky: {
    name: 'Nicky Wilkinson',
    role: 'Grantmaking Lead, Firetree Philanthropy',
    photo: 'https://cdn.magicpatterns.com/uploads/e2qouD1P2hNjAHsS33szdn/nicky.jpg',
    bio: "Nicky Wilkinson leads Firetree Philanthropy's grantmaking across South and Southeast Asia, working with grassroots organisations and networks to build more equitable futures in the communities Firetree supports. She joined Firetree after a career spanning social innovation, international development, and fundraising — including roles as Regional Partnerships Director for the British Council across 14 Asian markets, Fund Manager at Nesta (the UK's innovation foundation), and External Relations Director at the youth charity Changemakers. Earlier in her career she worked across sub-Saharan Africa and South Asia in international development. Nicky is a founding trustee of the GravityLight Foundation and an alumna of the ASEAN Leaders Programme."
  }
};
export function openTeamBioModal(memberId: string) {
  window.dispatchEvent(new CustomEvent('open-team-bio-modal', {
    detail: memberId
  }));
}
export function TeamBioModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [member, setMember] = useState<TeamMember | null>(null);
  const scrollYRef = useRef(0);
  const handleOpen = useCallback((e: Event) => {
    const memberId = (e as CustomEvent).detail as string;
    const found = teamMembers[memberId];
    if (found) {
      setMember(found);
      setIsOpen(true);
      scrollYRef.current = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.inset = '0';
      document.body.style.width = '100%';
    }
  }, []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.inset = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollYRef.current);
  }, []);
  useEffect(() => {
    window.addEventListener('open-team-bio-modal', handleOpen);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('open-team-bio-modal', handleOpen);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleOpen, handleClose, isOpen]);
  if (!isOpen || !member) return null;
  return <>
      {/* Mobile: full-screen scrollable page */}
      <div className="md:hidden fixed inset-0 z-[9998] bg-brand-cream overflow-y-auto" data-id="element-218">
        {/* Close Button — fixed position */}
        <button onClick={handleClose} className="fixed top-4 right-4 p-2.5 bg-brand-cream/80 backdrop-blur-sm text-brand-brown hover:text-brand-red transition-colors rounded-full hover:bg-brand-shape/20 z-[9999]" aria-label="Close modal" data-id="element-219">
          <X className="w-5 h-5" data-id="element-220" />
        </button>

        {/* Photo */}
        {member.photo ? <div className="relative w-full h-[50vh]" data-id="element-221">
            <img src={member.photo} alt={member.name} className="w-full h-full object-cover object-top" data-id="element-222" />
          </div> : <div className="w-full h-[40vh] bg-brand-card/40 flex items-center justify-center" data-id="element-223">
            <div className="w-32 h-32 rounded-full bg-brand-shape/30 flex items-center justify-center" data-id="element-224">
              <UserIcon className="w-16 h-16 text-brand-brown/40" strokeWidth={1} data-id="element-225" />
            </div>
          </div>}

        {/* Bio content */}
        <div className="p-6 pb-12" data-id="element-226">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-gold" data-id="element-227">
            {member.role}
          </span>
          <h3 className="text-2xl font-serif text-brand-charcoal mt-2 mb-6" data-id="element-228">
            {member.name}
          </h3>
          <div className="space-y-4" data-id="element-229">
            {member.bio.split('\n\n').map((paragraph, i) => <p key={i} className="text-brand-brown leading-relaxed" data-id="element-230">
                {paragraph}
              </p>)}
          </div>
        </div>
      </div>

      {/* Desktop: centered modal */}
      <div className={`hidden md:flex fixed inset-0 z-[9998] items-center justify-center p-6 transition-all duration-500 ease-out ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-231">
        {/* Backdrop */}
        <div className={`absolute inset-0 bg-brand-charcoal/60 transition-all duration-500 ease-out ${isOpen ? 'backdrop-blur-md' : 'backdrop-blur-none'}`} onClick={handleClose} data-id="element-232" />

        {/* Modal Card */}
        <div className={`relative w-full max-w-4xl bg-brand-cream shadow-2xl overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${isOpen ? 'scale-100 translate-y-0 opacity-100' : 'scale-[0.97] translate-y-6 opacity-0'}`} onClick={e => e.stopPropagation()} data-id="element-233">
          {/* Close Button */}
          <button onClick={handleClose} className="absolute top-4 right-4 p-2.5 text-brand-brown hover:text-brand-red transition-colors rounded-full hover:bg-brand-shape/20 z-20" aria-label="Close modal" data-id="element-234">
            <X className="w-5 h-5" data-id="element-235" />
          </button>

          <div className="grid grid-cols-3" data-id="element-236">
            {/* Left — Photo */}
            <div className="relative min-h-full bg-brand-card/40 flex items-center justify-center" data-id="element-237">
              {member.photo ? <img src={member.photo} alt={member.name} className="w-full h-full object-cover object-top absolute inset-0" data-id="element-238" /> : <div className="w-40 h-40 rounded-full bg-brand-shape/30 flex items-center justify-center" data-id="element-239">
                  <UserIcon className="w-20 h-20 text-brand-brown/40" strokeWidth={1} data-id="element-240" />
                </div>}
            </div>

            {/* Right — Bio content */}
            <div className="col-span-2 p-12 max-h-[70vh] overflow-y-auto" data-id="element-241">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-gold" data-id="element-242">
                {member.role}
              </span>
              <h3 className="text-3xl font-serif text-brand-charcoal mt-2 mb-6" data-id="element-243">
                {member.name}
              </h3>
              <div className="space-y-4" data-id="element-244">
                {member.bio.split('\n\n').map((paragraph, i) => <p key={i} className="text-brand-brown leading-relaxed" data-id="element-245">
                    {paragraph}
                  </p>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}